<!-- eslint-disable vue/no-confusing-v-for-v-if -->
<template xmlns:v-slot="http://www.w3.org/1999/XSL/Transform">
  <main v-if="guestProfile != null" class="main main--has-floating-buttons">
    <portal to="headerTitle">
      {{ $t('pageGuestProfile.title.guest') }} #{{ guestProfileNumber }}
    </portal>

    <card :title="$t('pageGuestProfile.card.name')" cols="12 6@tablet 4@desktop" rows="1 4@desktop">
      <hm-card-inner>
        <hm-input-select v-model="guestProfile.type" :label="$t('pageGuestProfile.type')" name="type" enum-name="ProfileType" />
        <hm-input v-if="guestProfile.type !== 'Guest'"
                  :lazy="true"
                  v-model="guestProfile.companyName"
                  :label="$t('pageGuestProfile.companyName')"
                  name="companyName" />
        <hm-input :lazy="true" v-model="guestProfile.namePrefix" :label="$t('pageGuestProfile.namePrefix')" name="namePrefix" />
        <hm-input :lazy="true" v-model="guestProfile.firstName" :label="$t('pageGuestProfile.firstName')" name="firstName" />
        <hm-input :lazy="true" v-model="guestProfile.middleName" :label="$t('pageGuestProfile.middleName')" name="middleName" />
        <hm-input :lazy="true" v-model="guestProfile.lastName" :label="$t('pageGuestProfile.lastName')" name="lastName" />
        <hm-input :lazy="true" v-model="guestProfile.nameSuffix" :label="$t('pageGuestProfile.nameSuffix')" name="nameSuffix" />
      </hm-card-inner>
      <hm-card-inner v-if="hasRole('ROLE_SYSADMIN')">
        <hm-input v-if="!isEmpty(guestProfileImportId)" :disabled="true" :value="guestProfileImportId" label="Import ID" />
        <hm-input :disabled="true" :value="guestProfile.createdAt | toLocalDateTime" label="Created at" />
      </hm-card-inner>
    </card>

    <card :title="$t('pageGuestProfile.card.options')" cols="12 6@tablet 4@desktop" rows="1 4@desktop">
      <hm-input-select v-model="guestProfile.status" :label="$t('pageGuestProfile.status')" name="status" enum-name="ProfileStatus" />
      <hm-input-date v-if="guestProfile.type === 'Guest'"
                     :required="false"
                     v-model="guestProfile.dateOfBirth"
                     :label="$t('pageGuestProfile.dateOfBirth')"
                     name="dateOfBirth" />
      <hm-gender v-model="guestProfile.gender" :label="$t('pageGuestProfile.gender')" name="gender" />
      <hm-select v-model="guestProfile.primaryCommunicationLanguage" :options="getI18nLanguages($shortLocale)" :label="$t('pageGuestProfile.primaryCommunicationLanguage')" name="primaryCommunicationLanguage" />
      <hm-select v-if="guestProfile.type === 'Guest'"
                 v-model="guestProfile.nationality"
                 :options="getI18nCountries($shortLocale, [guestProfile.nationality, ...(guestProfile.addresses || []).map(it => it.country)])"
                 :label="$t('pageGuestProfile.nationality')"
                 name="nationality"
                 hidden="mobile" />
      <hm-multiselect v-model="guestProfile.categories"
                      :options="hotelSettings.guestCategories.all()"
                      :label="$t('pageGuestProfile.guestCategories')"
                      name="guestCategories"
                      label-expression="${option.name[$shortLocale]}" />
      <hm-input :lazy="true" v-model="guestProfile.profession" :label="$t('pageGuestProfile.profession')" name="profession" />
    </card>

    <card :title="$t('pageGuestProfile.card.remarks')" cols="12 6@tablet 4@desktop" rows="1 2@desktop">
      <hm-col :grid="true" cols="12" rows="2">
        <hm-textarea :lazy="true"
                     v-model="guestProfile.guestRequests"
                     :input-rows="4"
                     :label="$t('pageGuestProfile.guestRequests')"
                     name="guestRequests" />
        <hm-textarea :lazy="true"
                     v-model="guestProfile.internalComment"
                     :input-rows="4"
                     :label="$t('pageGuestProfile.internalComment')"
                     name="internalComment" />
      </hm-col>
    </card>

    <card :title="$t('pageGuestProfile.card.optInsOptOuts')" cols="12 6@tablet 4@desktop" rows="1 2@desktop">
      <hm-card-inner>
        <template v-for="optIn in optInValues">
          <hm-input-toggle v-model="guestProfile.optIns[optIn.id]" :label="optIn.name[$shortLocale]" :key="optIn.id" name="optIn" />
        </template>
        <template v-for="optIn in optOutValues">
          <hm-input-toggle v-model="guestProfile.optOuts[optIn.id]" :label="optIn.name[$shortLocale]" :key="optIn.id" name="optOut" />
        </template>
      </hm-card-inner>
    </card>

    <card ref="addresses" :title="$t('pageGuestProfile.card.addresses')" cols="12 6@tablet 4@desktop">
      <hm-card-inner v-for="address in guestProfile.addresses"
                     :key="address.id"
                     :deletable="guestProfile.addresses.length > 1"
                     :class="[{'highlight': address.new}]"
                     :title="$t('pageGuestProfile.card.address')"
                     @delete="deleteAddress(address.id)">
        <hm-input-select v-model="address.type"
                         :label="$t('pageGuestProfile.addressType')"
                         name="addressType"
                         enum-name="AddressType"
                         cols="12" />
        <hm-textarea :lazy="true"
                     v-model="address.address"
                     :input-rows="2"
                     :label="$t('pageGuestProfile.address')"
                     name="address"
                     cols="12" />
        <hm-input-zip-city :zip="address.zipCode"
                           :city="address.city"
                           :country="address.country"
                           @update:zip="address.zipCode = $event"
                           @update:city="address.city = $event"
                           @update:country="address.country = $event" />
        <hm-select :lazy="true"
                   v-model="address.country"
                   :options="getI18nCountries($shortLocale, [address.country, guestProfile.nationality, ...(guestProfile.addresses || []).map(it => it.country)])"
                   :label="$t('pageGuestProfile.country')"
                   name="country"
                   hidden="mobile" />
      </hm-card-inner>
    </card>

    <card ref="emailAddresses" :title="$t('pageGuestProfile.card.emailAddresses')" cols="12 6@tablet 4@desktop">
      <hm-card-inner v-for="emailAddress in guestProfile.emailAddresses"
                     :key="emailAddress.id"
                     :deletable="guestProfile.emailAddresses.length > 1"
                     :class="[{'highlight': emailAddress.new}]"
                     :title="$t('pageGuestProfile.card.emailAddress')"
                     @delete="deleteEmailAddress(emailAddress.id)">
        <hm-input :lazy="true" v-model="emailAddress.emailAddress" :label="$t('pageGuestProfile.emailAddress')" name="emailAddress" />
        <hm-input-toggle v-if="guestProfile.emailAddresses.length > 1"
                         v-model="emailAddress.primary"
                         :label="$t('pageGuestProfile.primaryEmail')"
                         :disabled="emailAddress.primary"
                         cols="12"
                         @input="onChangePrimaryEmailAddress(emailAddress.id, $event)" />
      </hm-card-inner>
    </card>

    <card ref="phoneNumbers" :title="$t('pageGuestProfile.card.phoneNumbers')" cols="12 6@tablet 4@desktop">
      <hm-card-inner v-for="phoneNumber in guestProfile.phoneNumbers"
                     :key="phoneNumber.id"
                     :deletable="guestProfile.phoneNumbers.length > 1"
                     :class="[{'highlight': phoneNumber.new}]"
                     :title="$t('pageGuestProfile.card.phoneNumber')"
                     @delete="deletePhoneNumber(phoneNumber.id)">
        <hm-input-select v-model="phoneNumber.type"
                         :label="$t('pageGuestProfile.phoneType')"
                         name="phoneType"
                         enum-name="PhoneType"
                         cols="12" />
        <hm-input :lazy="true" v-model="phoneNumber.phoneNumber" :label="$t('pageGuestProfile.phoneNumber')" name="phoneNumber" />
        <hm-input-toggle v-if="guestProfile.phoneNumbers.length > 1"
                         v-model="phoneNumber.primary"
                         :label="$t('pageGuestProfile.primaryPhoneNumber')"
                         cols="12" />
      </hm-card-inner>
    </card>


    <tabable-card v-if="hasLicense('GuestHistory')"
                  id="additional-guests"
                  :key="additionalGuestCount"
                  cols="12"
                  min-height="30rem">
      <tabable-card-content :display="!isEmpty(guestProfile.additionalGuests)" :key="additionalGuestCount" :title="$t('pageGuestProfile.card.fellowTravellers')">
        <template v-for="(guest, index) in guestProfile.additionalGuests">
          <hm-card-inner :title="$t('pageGuestProfile.card.person', { idx: index+1 })" :deletable="true" :key="guest.id" @delete="deleteGuestDetail(guest)">
            <hm-input :lazy="true"
                      v-model="guest.firstName"
                      :label="$t('pageGuestProfile.firstName')"
                      name="firstName"
                      cols="12 3@tablet" />
            <hm-input :lazy="true"
                      v-model="guest.lastName"
                      :label="$t('pageGuestProfile.lastName')"
                      name="lastName"
                      cols="12 3@tablet" />
            <hm-input-date :required="false"
                           v-model="guest.dateOfBirth"
                           :label="$t('pageGuestProfile.dateOfBirth')"
                           name="dateOfBirth"
                           cols="12 3@tablet" />
            <hm-input-number :value="guest.dateOfBirth | toAge"
                             :disabled="true"
                             :label="$t('pageGuestProfile.age')"
                             name="age"
                             cols="12 3@tablet" />
          </hm-card-inner>
        </template>
      </tabable-card-content>
      <tabable-card-content :title="$t('pageGuestProfile.card.correspondence')">
        <hm-card-inner :full-size="true">
          <hm-col v-if="!isNil(correspondence)" cols="12">
            <correspondence-table :correspondence="correspondence" />
          </hm-col>
        </hm-card-inner>
      </tabable-card-content>
      <tabable-card-content :title="$t('pageGuestProfile.card.reservations')" @show="loadReservations">
        <spinner-overlay v-if="isNil(reservations)" />
        <hm-card-inner :full-size="true">
          <hm-col v-if="!isNil(reservations)" cols="12">
            <paginated-table :rows="reservations" columns="7" min-width="600px">
              <template slot="table-head">
                <div class="hm-table__th">{{ $t('pageGuestProfile.reservations.reservationNumber') }}</div>
                <div class="hm-table__th">{{ $t('pageGuestProfile.reservations.arrivalDate') }}</div>
                <div class="hm-table__th">{{ $t('pageGuestProfile.reservations.departureDate') }}</div>
                <div class="hm-table__th">{{ $t('pageGuestProfile.reservations.numAdults') }}</div>
                <div class="hm-table__th">{{ $t('pageGuestProfile.reservations.numChildren') }}</div>
                <div class="hm-table__th">{{ $t('pageGuestProfile.reservations.status') }}</div>
                <div class="hm-table__th">{{ $t('pageGuestProfile.reservations.roomNumber') }}</div>
              </template>
              <template v-slot:default="slotProps">
                <template v-for="row in slotProps.currentPage">
                  <div class="hm-table__td" @click="openReservation(row.id)">{{ row.reservationNumber }}</div>
                  <div class="hm-table__td" @click="openReservation(row.id)">{{ row.checkInDate | moment($moment.localeData().longDateFormat('L')) }}</div>
                  <div class="hm-table__td" @click="openReservation(row.id)">{{ row.checkOutDate | moment($moment.localeData().longDateFormat('L')) }}</div>
                  <div class="hm-table__td" @click="openReservation(row.id)">{{ row.numAdults }}</div>
                  <div class="hm-table__td" @click="openReservation(row.id)">{{ row.numChildren }}</div>
                  <div class="hm-table__td" @click="openReservation(row.id)">{{ $t(`ReservationStatus.${row.status}`) }}</div>
                  <div class="hm-table__td" @click="openReservation(row.id)">{{ row.roomNumber }}</div>
                </template>
              </template>
            </paginated-table>
          </hm-col>
        </hm-card-inner>
      </tabable-card-content>
      <tabable-card-content :title="$t('pageGuestProfile.card.offers')" @show="loadOffers">
        <spinner-overlay v-if="isNil(offers)" />
        <hm-card-inner :full-size="true">
          <hm-col v-if="!isNil(offers)" cols="12">
            <paginated-table :rows="offers" columns="5" min-width="600px">
              <template slot="table-head">
                <div class="hm-table__th">{{ $t('pageGuestProfile.offers.offerNumber') }}</div>
                <div class="hm-table__th">{{ $t('pageGuestProfile.offers.arrivalDate') }}</div>
                <div class="hm-table__th">{{ $t('pageGuestProfile.offers.departureDate') }}</div>
                <div class="hm-table__th">{{ $t('pageGuestProfile.offers.roomCount') }}</div>
                <div class="hm-table__th">{{ $t('pageGuestProfile.offers.lastModifiedAt') }}</div>
              </template>
              <template v-slot:default="slotProps">
                <template v-for="row in slotProps.currentPage">
                  <div class="hm-table__td" @click="openOffer(row.id)">{{ row.offerNumber }}</div>
                  <div class="hm-table__td" @click="openOffer(row.id)">{{ row.checkInDate | moment($moment.localeData().longDateFormat('L')) }}</div>
                  <div class="hm-table__td" @click="openOffer(row.id)">{{ row.checkOutDate | moment($moment.localeData().longDateFormat('L')) }}</div>
                  <div class="hm-table__td" @click="openOffer(row.id)">{{ row.numberOfRooms }}</div>
                  <div class="hm-table__td" @click="openOffer(row.id)">{{ row.lastModified | toLocalDateTime }}</div>
                </template>
              </template>
            </paginated-table>
          </hm-col>
        </hm-card-inner>
      </tabable-card-content>
    </tabable-card>

    <card v-if="hasRole('ROLE_ADMIN')"
          v-once
          :title="$t('pageGuestProfile.card.editHistory')"
          cols="12"
          card-type="collapsible">
      <hm-card-inner :full-size="true">
        <history-table :history="guestProfile.history" />
      </hm-card-inner>
    </card>

    <hm-floating-buttons>
      <!--
      <hm-floating-button icon="fill-more" color="gray" label="Mehr">
        <hm-floating-child-button icon="plus" label="Gästeprofil exportieren" color="orange" />
      </hm-floating-button>
      -->
      <hm-floating-button :label="$t('pageGuestProfile.actions.more')" icon="plus" color="blue">
        <hm-floating-child-button :label="$t('pageGuestProfile.actions.addEmail')" icon="plus" color="blue" @click="$router.push({ name: 'guestEmail', params: $route.params})" />
        <hm-floating-child-button :label="$t('pageGuestProfile.actions.addAdditionalGuest')" icon="plus" color="blue" @click="addAdditionalGuest(null)" />
        <hm-floating-child-button :label="$t('pageGuestProfile.actions.addAddress')" icon="plus" color="blue" @click="addAddress()" />
        <hm-floating-child-button :label="$t('pageGuestProfile.actions.addPhoneNumber')" icon="plus" color="blue" @click="addPhoneNumber" />
        <hm-floating-child-button :label="$t('pageGuestProfile.actions.addEmailAddress')" icon="plus" color="blue" @click="addEmailAddress" />
      </hm-floating-button>
      <hm-floating-button v-if="hasLicense('GuestProfileFolios') && hasLicense('Invoicing')"
                          :label="$t('pageGuestProfile.actions.openFolios')"
                          icon="receipt"
                          color="orange"
                          @click="$router.push({name: 'guestFolios', params: {guestProfileId: guestProfileId}})" />
    </hm-floating-buttons>
  </main>
</template>

<script>
/* eslint func-names: 0 */

import HotelmeisterMixin from '@/mixins/HotelmeisterMixin';
import {
  isArray,
  join,
  isNil,
  isEmpty,
  get,
  filter,
  find,
  nth,
  template,
  clone,
  cloneDeep,
  last,
  findIndex,
  first,
  remove,
  uniqBy, debounce, map, sortBy,
} from 'lodash';
import HmCol from '../lib/layout/HmCol';
import HmInput from '../lib/inputs/HmInput';
import SaveWatchMixin from '@/mixins/SaveWatchMixin';
import messages from '@/protobuf/index';
import { util } from 'protobufjs/minimal';
import HmTextarea from '../lib/inputs/HmTextarea';
import HmFloatingChildButton from '../lib/inputs/HmFloatingChildButton';
import HmCardInnerLine from '../lib/layout/HmCardInnerLine';
import uuid from 'uuid';
import PaginatedTable from '../lib/table/PaginatedTable';
import TabableCardContent from '../lib/layout/TabableCardContent';
import HmCardInner from '../lib/layout/HmCardInner';
import SpinnerOverlay from '../lib/util/SpinnerOverlay';
import TimerMixin from '../../mixins/TimerMixin';
import HmInputZipCity from '../lib/inputs/HmInputZipCity';

export default {
  name: 'PageGuestProfile',
  components: {
    HmInputZipCity,
    SpinnerOverlay,
    HmCardInner,
    TabableCardContent,
    PaginatedTable,
    HmCardInnerLine,
    HmFloatingChildButton,
    HmTextarea,
    HmInput,
    HmCol,
  },

  mixins: [HotelmeisterMixin, SaveWatchMixin, TimerMixin],
  props: ['test'],

  autosave: [
    {
      name: 'guestProfile',
      variable: 'guestProfile',
      fn: function (cbSaveComplete) {
        const guestProfile = cloneDeep(this.guestProfile);

        const tempOptIns = guestProfile.optIns;
        guestProfile.optIns = [];
        this.optInValues.forEach((it) => {
          if (!isNil(tempOptIns) && tempOptIns[it.id] === true) {
            guestProfile.optIns.push(it.id);
          }
        });

        const tempOptOuts = guestProfile.optOuts;
        guestProfile.optOuts = [];
        this.optOutValues.forEach((it) => {
          if (!isNil(tempOptOuts) && tempOptOuts[it.id] === false) {
            guestProfile.optOuts.push(it.id);
          }
        });


        this.axios.put(this.serviceUrls.guest.get(this, this.guestProfileId), guestProfile)
          .then((response) => {
            this.showMessages(response);
            cbSaveComplete();
            this.updateHistory();
          })
          .catch((error) => {
            this.showMessages(error.response);
            cbSaveComplete();
          });
      },
      exclude: ['history'],
    },
  ],


  data() {
    return {
      guestProfile: null,
      guestProfileId: null,
      correspondence: null,
      reservations: null,
      offers: null,
      loadingReservations: false,
      loadingOffers: false,
      additionalGuestCount: 0,
    };
  },

  computed: {
    guestProfileNumber: function () {
      return last(this.guestProfileId.split('::'));
    },
    optInValues: function () {
      return isNil(this.hotelSettings.optIns) ? [] : this.hotelSettings.optIns.filter(it => it.defaultValue === false);
    },
    optOutValues: function () {
      return isNil(this.hotelSettings.optIns) ? [] : this.hotelSettings.optIns.filter(it => it.defaultValue === true);
    },
    guestProfileImportId: function () {
      if (isEmpty(this.guestProfile.refKeys)) return null;
      const refKey = this.guestProfile.refKeys.find(it => it.key === 'PMS_IMPORT_GUEST');
      if (isEmpty(refKey)) return null;

      return refKey.value;
    },
  },

  watch: {
  },

  created: function () {
    this.guestProfileId = this.$route.params.guestProfileId;
    this.updateGuestProfile();
    this.updateCorrespondence();
    // this.updateReservations();
    // this.updateOffers();
  },

  methods: {
    deleteGuestDetail: function (guest) {
      this.guestProfile.additionalGuests.splice(this.guestProfile.additionalGuests.indexOf(guest), 1);

      this.additionalGuestCount = isNil(this.guestProfile) ? 0 : this.guestProfile.additionalGuests.length;
    },
    addAdditionalGuest: function (guest = null) {
      if (isNil(this.guestProfile.additionalGuests)) {
        this.guestProfile.additionalGuests = [];
      }

      if (isNil(guest)) {
        this.guestProfile.additionalGuests.push({
          id: uuid.v4(),
          firstName: null,
          lastName: null,
          dateOfBirth: null,
        });
      } else {
        this.guestProfile.additionalGuests.push(guest);
      }

      this.additionalGuestCount = isNil(this.guestProfile) ? 0 : this.guestProfile.additionalGuests.length;
    },
    loadReservations: function () {
      if (isNil(this.reservations) && !this.loadingReservations) {
        this.loadingReservations = true;
        this.updateReservations()
          .then(() => {
            this.loadingReservations = false;
          })
          .catch(() => {
            this.loadingReservations = false;
          });
      }
    },
    loadOffers: function () {
      if (isNil(this.offers) && !this.loadingOffers) {
        this.loadingOffers = true;
        this.updateOffers()
          .then(() => {
            this.loadingOffers = false;
          })
          .catch(() => {
            this.loadingOffers = false;
          });
      }
    },
    openReservation: function (referenceId) {
      this.$router.push({ name: 'reservation', params: { reservationId: referenceId } });
    },
    openOffer: function (referenceId) {
      this.$router.push({ name: 'offer', params: { guestOfferId: referenceId } });
    },
    isEmpty: function (value) {
      return isEmpty(value);
    },
    intArray: function (value, offset = 0) {
      return [...Array(value).keys()].map(x => x + offset);
    },
    updateHistory: debounce(
      () => {
        // return this.axios
        //   .get(this.serviceUrls.guest.getHistory(this, this.guestProfileId))
        //   .then((response) => {
        //     this.guestProfile.history = response.data.data.history;
        //   });
      },
      2000,
    ),
    updateGuestProfile: function () {
      return this.axios
        .get(this.serviceUrls.guest.getFull(this, this.guestProfileId))
        .then((response) => {
          const guestProfile = response.data.data;
          if (isNil(guestProfile.additionalGuests)) guestProfile.additionalGuests = [];

          const tempOptIns = guestProfile.optIns;
          guestProfile.optIns = {};
          this.optInValues.forEach((it) => {
            if (!isNil(tempOptIns) && tempOptIns.includes(it.id)) {
              guestProfile.optIns[it.id] = true;
            } else {
              guestProfile.optIns[it.id] = false;
            }
          });

          const tempOptOuts = guestProfile.optOuts;
          guestProfile.optOuts = {};
          this.optOutValues.forEach((it) => {
            if (!isNil(tempOptOuts) && tempOptOuts.includes(it.id)) {
              guestProfile.optOuts[it.id] = false;
            } else {
              guestProfile.optOuts[it.id] = true;
            }
          });

          // for backwards compatibility - make sure if an OptOut is stored, that it always overrules the optIn
          if (!isNil(tempOptOuts)) {
            tempOptOuts.forEach((it) => {
              if (guestProfile.optIns[it] === true) {
                guestProfile.optIns[it] = false;
              }
            });
          }

          this.guestProfile = guestProfile;

          if (isEmpty(this.guestProfile.addresses)) this.addAddress();
          if (isEmpty(this.guestProfile.emailAddresses)) this.addEmailAddress();
          if (isEmpty(this.guestProfile.phoneNumbers)) this.addPhoneNumber();

          this.additionalGuestCount = isNil(this.guestProfile) ? 0 : this.guestProfile.additionalGuests.length;
        });
    },
    updateCorrespondence: function () {
      return this.axios
        .get(this.serviceUrls.guest.correspondence(this, this.guestProfileId))
        .then((response) => {
          this.correspondence = uniqBy(response.data.data, 'id');
        });
    },
    updateReservations: function () {
      return this.axios
        .get(this.serviceUrls.guest.reservations(this, this.guestProfileId))
        .then((response) => {
          this.reservations = response.data.data;
        });
    },
    updateOffers: function () {
      return this.axios
        .get(this.serviceUrls.guest.offers(this, this.guestProfileId))
        .then((response) => {
          this.offers = response.data.data;
        });
    },
    addAddress: function () {
      this.guestProfile.addresses.push({
        type: 'Home',
        id: uuid.v4(),
        new: true,
        mailingAddress: false,
      });
      this.$nextTick(() => {
        this.setTimeout(() => {
          this.glowEl(this.$refs.addresses.$el.querySelector('.card__inner:last-child'));
        }, 250);
      });
    },
    addEmailAddress: function () {
      this.guestProfile.emailAddresses.push({
        id: uuid.v4(),
        primary: false,
        new: true,
        confirmed: false,
      });
      this.$nextTick(() => {
        this.setTimeout(() => {
          this.glowEl(this.$refs.emailAddresses.$el.querySelector('.card__inner:last-child'));
        }, 250);
      });
    },
    addPhoneNumber: function () {
      this.guestProfile.phoneNumbers.push({
        type: 'Home',
        id: uuid.v4(),
        new: true,
        primary: false,
      });
      this.$nextTick(() => {
        this.setTimeout(() => {
          this.glowEl(this.$refs.phoneNumbers.$el.querySelector('.card__inner:last-child'));
        }, 250);
      });
    },
    deleteAddress: function (recordId) {
      this.deleteById('addresses', recordId);
    },
    deleteEmailAddress: function (recordId) {
      this.deleteById('emailAddresses', recordId);
    },
    deletePhoneNumber: function (recordId) {
      this.deleteById('phoneNumbers', recordId);
    },
    deleteById: function (type, id) {
      this.guestProfile[type].splice(this.guestProfile[type].indexOf(this.guestProfile[type].find(it => it.id === id)), 1);
    },
    onChangePrimaryEmailAddress: function (emailAddressId, newValue) {
      if (newValue === true) {
        this.guestProfile.emailAddresses.forEach((val, idx) => {
          if (val.id !== emailAddressId) {
            this.$set(this.guestProfile.emailAddresses[idx], 'primary', false);
          }
        });
      } else if (this.guestProfile.emailAddresses.length > 0 && this.guestProfile.emailAddresses.filter(it => it.primary === true).length === 0) {
        this.$nextTick(() => {
          this.$set(this.guestProfile.emailAddresses[0], 'primary', true);
        });
      }
    },
  },
};
</script>

<!-- Scoped SCSS  -->
<style lang="scss" scoped>
  @import '~styles/import';
</style>

<style lang="scss">
  @import '~styles/import';
</style>



// WEBPACK FOOTER //
// src/components/pages/PageGuestProfile.vue